import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Loader2, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFormattedDate } from '../i18n/dateUtils';
import { adminApi } from '../utils/api';
import type { User } from '../types';

const UserManagement = () => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { formatDate } = useFormattedDate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);

  // 创建用户表单
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user',
    daily_quota: 100,
  });

  // 编辑表单
  const [editForm, setEditForm] = useState({
    role: 'user',
    daily_quota: 100,
    is_active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getUsers();
      setUsers(response.data.users);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('userManagement.error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminApi.createUser(newUser);
      setShowCreateModal(false);
      setNewUser({ username: '', password: '', role: 'user', daily_quota: 100 });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('userManagement.error.createFailed'));
    }
  };

  const handleUpdateUser = async (username: string) => {
    try {
      await adminApi.updateUser(username, editForm);
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('userManagement.error.updateFailed'));
    }
  };

  const handleDeleteUser = async (username: string) => {
    if (!confirm(t('userManagement.confirmDelete', { username }))) return;

    try {
      await adminApi.deleteUser(username);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('userManagement.error.deleteFailed'));
    }
  };

  const startEdit = (user: User) => {
    setEditingUser(user.username);
    setEditForm({
      role: user.role,
      daily_quota: user.daily_quota,
      is_active: user.is_active,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('userManagement.title')}</h1>
          <p className="text-gray-600 mt-1">{t('userManagement.description')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('userManagement.createButton')}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          {error}
        </div>
      )}

      {/* 用户列表 */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.username')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.dailyQuota')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.usedToday')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.createdAt')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('userManagement.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.username} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900">
                      {user.username}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser === user.username ? (
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="input py-1 px-2 text-sm"
                      >
                        <option value="user">{tCommon('roles.user')}</option>
                        <option value="admin">{tCommon('roles.admin')}</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {user.role === 'admin' ? tCommon('roles.admin') : tCommon('roles.user')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser === user.username ? (
                      <select
                        value={editForm.is_active.toString()}
                        onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value === 'true' })}
                        className="input py-1 px-2 text-sm"
                      >
                        <option value="true">{tCommon('status.enabled')}</option>
                        <option value="false">{tCommon('status.disabled')}</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.is_active ? tCommon('status.enabled') : tCommon('status.disabled')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingUser === user.username ? (
                      <input
                        type="number"
                        value={editForm.daily_quota}
                        onChange={(e) => setEditForm({ ...editForm, daily_quota: parseInt(e.target.value) })}
                        className="input py-1 px-2 text-sm w-24"
                        min="10"
                        max="10000"
                      />
                    ) : (
                      <span className="text-gray-900">{user.daily_quota}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-gray-900">{user.used_today}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {editingUser === user.username ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleUpdateUser(user.username)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(user)}
                          className="text-blue-600 hover:text-blue-900"
                          disabled={user.username === 'admin'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.username)}
                          className="text-red-600 hover:text-red-900"
                          disabled={user.username === 'admin'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 创建用户弹窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">{t('userManagement.createModal.title')}</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('userManagement.createModal.usernameLabel')}
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="input"
                  required
                  minLength={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('userManagement.createModal.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="input"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('userManagement.createModal.roleLabel')}
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="input"
                >
                  <option value="user">{tCommon('roles.user')}</option>
                  <option value="admin">{tCommon('roles.admin')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('userManagement.createModal.dailyQuotaLabel')}
                </label>
                <input
                  type="number"
                  value={newUser.daily_quota}
                  onChange={(e) => setNewUser({ ...newUser, daily_quota: parseInt(e.target.value) })}
                  className="input"
                  min="10"
                  max="10000"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 btn-secondary"
                >
                  {t('userManagement.createModal.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                >
                  {t('userManagement.createModal.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
